import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { FilterBar, FilterField, Input, RadixSelect } from '@/components/ui';
import type { ArtistStatusEnum, ArtistTypeEnum } from '@/lib/api-admin/types';
import { useArtistFiltersStore } from '@/stores/useArtistFiltersStore';

const DEBOUNCE_MS = 300;

const STATUS_OPTIONS: ReadonlyArray<ArtistStatusEnum> = ['ACTIVE', 'ARCHIVED'];
const TYPE_OPTIONS: ReadonlyArray<ArtistTypeEnum> = ['SOLO', 'GROUP'];

export const ArtistFiltersBar = () => {
  const { t } = useTranslation('artists');
  const keyword = useArtistFiltersStore((s) => s.keyword);
  const type = useArtistFiltersStore((s) => s.type);
  const status = useArtistFiltersStore((s) => s.status);
  const hasNoImage = useArtistFiltersStore((s) => s.hasNoImage);
  const setKeyword = useArtistFiltersStore((s) => s.setKeyword);
  const setType = useArtistFiltersStore((s) => s.setType);
  const setStatuses = useArtistFiltersStore((s) => s.setStatuses);
  const setHasNoImage = useArtistFiltersStore((s) => s.setHasNoImage);

  const [local, setLocal] = useState(keyword);

  useEffect(() => {
    if (local === keyword) return;
    const id = setTimeout(() => setKeyword(local), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [local, keyword, setKeyword]);

  const singleStatus: '' | ArtistStatusEnum =
    status.length === 1 ? (status[0] as ArtistStatusEnum) : '';

  const typeOptions = [
    { value: '', label: t('artists.filters.typeAll') },
    ...TYPE_OPTIONS.map((opt) => ({ value: opt, label: t(`artists.type.${opt}`) })),
  ];
  const statusOptions = [
    { value: '', label: t('artists.filters.statusAll') },
    ...STATUS_OPTIONS.map((opt) => ({ value: opt, label: t(`artists.status.${opt}`) })),
  ];

  return (
    <FilterBar
      aria-label={t('artists.filters.label')}
      data-testid="artist-filters"
      filters={
        <>
          <FilterField label={t('artists.filters.keyword')}>
            <Input
              id="artist-keyword"
              value={local}
              onChange={(e) => setLocal(e.target.value)}
              placeholder={t('artists.filters.keywordPlaceholder')}
              className="w-full"
              maxLength={100}
            />
          </FilterField>
          <FilterField label={t('artists.filters.type')}>
            <RadixSelect
              id="artist-type"
              value={type ?? ''}
              onChange={(v) => setType(v === '' ? null : (v as ArtistTypeEnum))}
              options={typeOptions}
              className="w-full"
            />
          </FilterField>
          <FilterField label={t('artists.filters.status')}>
            <RadixSelect
              id="artist-status"
              value={singleStatus}
              onChange={(v) => setStatuses(v === '' ? [] : [v as ArtistStatusEnum])}
              options={statusOptions}
              className="w-full"
            />
          </FilterField>
          {/* 체크박스 필터는 문구를 체크박스 옆에만 둔다 — 왼쪽 라벨 칸(72px·nowrap)에 같은 문구를 또 쓰면 넘쳐서 겹친다. 빈 라벨 칸이 남아 다른 필터와 줄은 맞는다. */}
          <FilterField label="">
            <label className="inline-flex items-center gap-1.5 text-xs text-[color:var(--content-default)]">
              <input
                type="checkbox"
                checked={hasNoImage}
                onChange={(e) => setHasNoImage(e.target.checked)}
              />
              {t('artists.filters.hasNoImage')}
            </label>
          </FilterField>
        </>
      }
    />
  );
};
